import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { useGetAllMaterials } from '../../../hooks/useMateriales';
import { useGetProviders } from '../../../lib/react-query/providerQuery';

const PROVIDER_SIN_DEFINIR = {
  _id: 'SIN_DEFINIR',
  shortName: 'Sin definir',
};

const createEmptyProvider = () => ({
  code: '',
  price: '',
  provider: '',
  allowDuplicateCode: false,
});

const createEmptyFormat = () => ({
  name: '',
  material: '',
  providers: [],
});

const CreateFormatToNewArteFinal = ({ formik, goBack }) => {
  const { data: allMaterials, isLoading: loadingMaterials } = useGetAllMaterials();
  const MATERIAL_SIN_MATERIAL = {
    _id: 'SIN_MATERIAL',
    nombre: 'Sin material',
  };
  const { providers, loadingProviders } = useGetProviders();

  const formats = formik.values.formats || [];
  const providerOptions = loadingProviders ? [] : [PROVIDER_SIN_DEFINIR, ...(providers?.data || [])];

  const handleAddFormat = () => {
    formik.setFieldValue('formats', [...formats, createEmptyFormat()]);
  };

  const handleRemoveFormat = (formatIndex) => {
    const updatedFormats = [...formats];
    updatedFormats.splice(formatIndex, 1);
    formik.setFieldValue('formats', updatedFormats);
  };

  const handleFormatFieldChange = (formatIndex, field, value) => {
    const updatedFormats = [...formats];
    updatedFormats[formatIndex] = {
      ...updatedFormats[formatIndex],
      [field]: value,
    };
    formik.setFieldValue('formats', updatedFormats);
  };

  const handleAddProvider = (formatIndex) => {
    const updatedFormats = [...formats];
    const currentProviders = updatedFormats[formatIndex].providers || [];

    updatedFormats[formatIndex] = {
      ...updatedFormats[formatIndex],
      providers: [...currentProviders, createEmptyProvider()],
    };

    formik.setFieldValue('formats', updatedFormats);
  };

  const handleRemoveProvider = (formatIndex, providerIndex) => {
    const updatedFormats = [...formats];
    const updatedProviders = [...updatedFormats[formatIndex].providers];
    updatedProviders.splice(providerIndex, 1);

    updatedFormats[formatIndex] = {
      ...updatedFormats[formatIndex],
      providers: updatedProviders,
    };

    formik.setFieldValue('formats', updatedFormats);
  };

  const handleProviderFieldChange = (formatIndex, providerIndex, field, value) => {
    const updatedFormats = [...formats];
    const updatedProviders = [...updatedFormats[formatIndex].providers];

    updatedProviders[providerIndex] = {
      ...updatedProviders[providerIndex],
      [field]: value,
    };

    updatedFormats[formatIndex] = {
      ...updatedFormats[formatIndex],
      providers: updatedProviders,
    };

    formik.setFieldValue('formats', updatedFormats);
  };

  return (
    <form onSubmit={formik.handleSubmit} style={{ minWidth: '60rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <Typography variant="h6">Formatos y proveedores</Typography>

        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddFormat}>
          Añadir formato
        </Button>
      </div>

      {formats.length === 0 && (
        <Typography variant="body2" style={{ marginBottom: '1rem' }}>
          No has añadido formatos. Puedes confirmar sin formatos si este artículo no los necesita.
        </Typography>
      )}

      {formats.map((format, formatIndex) => (
        <Paper
          key={formatIndex}
          elevation={2}
          style={{
            padding: '1rem',
            marginBottom: '1rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <Typography variant="subtitle1">Formato {formatIndex + 1}</Typography>

            <IconButton color="error" onClick={() => handleRemoveFormat(formatIndex)}>
              <DeleteIcon />
            </IconButton>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Medida"
                fullWidth
                value={format.name}
                onChange={(e) => handleFormatFieldChange(formatIndex, 'name', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={loadingMaterials ? [] : [MATERIAL_SIN_MATERIAL, ...(allMaterials?.data || [])]}
                getOptionLabel={(option) => option?.nombre || ''}
                value={
                  !format.material
                    ? MATERIAL_SIN_MATERIAL
                    : allMaterials?.data?.find((material) => material._id === format.material) || null
                }
                onChange={(event, newValue) => {
                  handleFormatFieldChange(
                    formatIndex,
                    'material',
                    newValue?._id === 'SIN_MATERIAL' ? '' : newValue?._id || '',
                  );
                }}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderInput={(params) => <TextField {...params} label="Material" fullWidth />}
                noOptionsText="Sin material"
                loading={loadingMaterials}
              />
            </Grid>

            <Grid item xs={12}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <Typography variant="subtitle2">Proveedores del formato</Typography>

                {!formik.values.isInformational && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddProvider(formatIndex)}
                  >
                    Añadir proveedor
                  </Button>
                )}
              </div>

              {!formik.values.isInformational && format.providers?.length === 0 && (
                <Typography variant="body2" style={{ marginBottom: '1rem', fontStyle: 'italic' }}>
                  Todavía no has añadido ningún proveedor para este formato.
                </Typography>
              )}

              {formik.values.isInformational && (
                <Typography variant="body2" style={{ marginBottom: '1rem', fontStyle: 'italic' }}>
                  No puedes añadir proveedores a este artículo que solo sea informativo.
                </Typography>
              )}

              {format.providers?.map((providerItem, providerIndex) => (
                <Paper key={providerIndex} variant="outlined" style={{ padding: '1rem', marginBottom: '1rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                    }}
                  >
                    <Typography variant="body1">Proveedor {providerIndex + 1}</Typography>

                    <IconButton color="error" onClick={() => handleRemoveProvider(formatIndex, providerIndex)}>
                      <DeleteIcon />
                    </IconButton>
                  </div>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Código Ariba"
                        fullWidth
                        value={providerItem.code}
                        onChange={(e) => handleProviderFieldChange(formatIndex, providerIndex, 'code', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Precio"
                        type="number"
                        fullWidth
                        value={providerItem.price}
                        onChange={(e) => handleProviderFieldChange(formatIndex, providerIndex, 'price', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        options={providerOptions}
                        getOptionLabel={(option) => option.shortName || ''}
                        value={providerOptions.find((provider) => provider._id === providerItem.provider) || null}
                        onChange={(event, newValue) => {
                          handleProviderFieldChange(
                            formatIndex,
                            providerIndex,
                            'provider',
                            newValue ? newValue._id : '',
                          );
                        }}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        renderInput={(params) => <TextField {...params} label="Proveedor" fullWidth />}
                        noOptionsText="Sin proveedor"
                        loading={loadingProviders}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!providerItem.allowDuplicateCode}
                            onChange={(e) =>
                              handleProviderFieldChange(
                                formatIndex,
                                providerIndex,
                                'allowDuplicateCode',
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label="Permitir repetir código"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </Paper>
      ))}

      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1rem' }}>
        <Button type="button" variant="contained" color="error" onClick={goBack}>
          Atrás
        </Button>

        <Button type="submit" variant="contained" style={{ backgroundColor: '#3C4145' }} endIcon={<SendIcon />}>
          Confirmar
        </Button>
      </div>
    </form>
  );
};

export default CreateFormatToNewArteFinal;
